import React from "react";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../../i18n";
import Select from "@ui/Select";
import useInput from "@ui/lib/hooks/useInput";
import { getEnhancedFaultStatusText, getFaultStatusText } from "../../../Fault";
import type { FaultEvent } from "../../../../../api/useFaultEvents";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { usePut } from "../../../../../api/useCall";
import Text from "@ui/Text";
import useToasts from "@ui/Toast/useToasts";
import type { FailureValueStatus } from "../../../Chart/PointMetadata";
import { generateFaultEventPayload, getNewDiagnosesStatus } from "diagnostics-domain/faultEvent";
interface P {
  projectId: string;
  event: FaultEvent;
  closeModal: () => void;
}

const faultStatusOptions: { value: FailureValueStatus; label: string }[] = [
  {
    value: "TO_BE_ANALYSED",
    label: getEnhancedFaultStatusText("TO_BE_ANALYSED"),
  },
  {
    value: "SUSPECTED",
    label: getEnhancedFaultStatusText("SUSPECTED"),
  },
  {
    value: "CONFIRMED",
    label: getEnhancedFaultStatusText("CONFIRMED"),
  },
  {
    value: "REJECTED",
    label: getEnhancedFaultStatusText("REJECTED"),
  },
  {
    value: "LEGACY_REJECTED",
    label: getEnhancedFaultStatusText("LEGACY_REJECTED"),
  },
];
const DefaultFaultStatus = "TO_BE_ANALYSED";
const ModalFormDetectionsStatusChange = ({ projectId, event, closeModal }: P) => {
  const { addToast } = useToasts();
  const mutateFaultsEvent = usePut("/projects/:projectId(uuid)/faultEvents");

  const {
    value: newFaultStatus,
    setValue: setNewFaultStatus,
    onChange,
    dirty,
  } = useInput(event.metadata.newDiagnosesStatus);

  const isDirty = event.metadata.newDiagnosesStatus !== newFaultStatus;

  const handleOnSubmit = React.useCallback(async () => {
    const faultEvent = generateFaultEventPayload({
      faultEvent: {
        ...event,
        newDiagnosesStatus: getNewDiagnosesStatus(newFaultStatus as FailureValueStatus),
      },
    });

    try {
      await mutateFaultsEvent({
        body: faultEvent,
        routeParams: { projectId },
      });
    } catch (e) {
      addToast("error", t("errors.backend_error"));
    }
    return true;
  }, [addToast, event, newFaultStatus, projectId, mutateFaultsEvent]);

  return (
    <ModalForm
      onRequestClose={closeModal}
      onSubmit={isDirty ? handleOnSubmit : false}
      okButtonName={t(
        "project.fault_event.evolution_chart.detections_status_change.modal.form.button.name",
      )}
      cancellable
    >
      <Text>
        {t("project.fault_event.evolution_chart.detections_status_change.modal.form.description", {
          toAnalysed: (
            <Text type="body1SemiBold" as="span">
              {getFaultStatusText(DefaultFaultStatus)}
            </Text>
          ),
        })}
      </Text>
      <Select
        label={t("project.fault_event.modal_form.select.status.label")}
        prompt=""
        defaultValue={event.metadata.newDiagnosesStatus}
        value={newFaultStatus}
        onChange={onChange}
        options={faultStatusOptions}
        clearable={false}
        revertable={dirty}
        revert={() => setNewFaultStatus(event.metadata.newDiagnosesStatus)}
      />
      {isDirty ? (
        <AdditionnalMessage
          status="INFO"
          text={t(
            "project.fault_event.evolution_chart.detections_status_change.modal.form.additionnal_message.repeat_detection",
          )}
        />
      ) : null}
    </ModalForm>
  );
};

export default ModalFormDetectionsStatusChange;
